import { SpecificRecommendations } from '../types';

export const constitutionalRecommendations: Record<string, SpecificRecommendations> = {
  // VATA CONSTITUTION
  'vata': {
    dietary: {
      foodsToFavor: [
        'Sweet, sour, and salty tastes',
        'Rice, wheat, oats (cooked and warm)',
        'Cooked root vegetables, asparagus, beets, carrots, sweet potatoes',
        'Sweet, juicy fruits like bananas, mangoes, grapes, berries',
        'Chicken, fish, eggs, tofu, mung beans, red lentils',
        'Warm milk, ghee, fresh cheese (in moderation)',
        'Sesame, almond, olive oil (liberal use)',
        'Ginger, cinnamon, cardamom, fennel, cumin (warming but not too hot)'
      ],
      foodsToAvoid: [
        'Raw vegetables and salads',
        'Dry, light foods (crackers, popcorn)',
        'Bitter vegetables (kale, broccoli in excess)',
        'Cold drinks and ice',
        'Excessive caffeine',
        'Beans (except mung and red lentils)'
      ],
      mealTiming: [
        'Regular meal times (never skip meals)',
        'Warm, cooked, moist foods',
        'Eat in calm, peaceful environment',
        'Favor hearty, nourishing meals',
        'Drink warm water throughout day'
      ]
    },
    lifestyle: {
      exercise: [
        'Gentle, grounding exercises',
        'Yoga (slow, steady practice)',
        'Walking, swimming, tai chi',
        'Avoid excessive or irregular exercise',
        'Exercise in warm environment'
      ],
      dailyRoutine: [
        'Consistent sleep schedule (bed by 10 PM, wake by 6 AM)',
        'Oil massage (abhyanga) daily with warm sesame oil',
        'Warm baths to calm nervous system',
        'Meditation and pranayama for mental stability',
        'Avoid overstimulation and multitasking'
      ]
    }
  },

  // PITTA CONSTITUTION
  'pitta': {
    dietary: {
      foodsToFavor: [
        'Sweet, bitter, and astringent (cooling tastes)',
        'Rice, wheat, barley, oats (cooling grains)',
        'Cucumbers, leafy greens, broccoli, cauliflower, asparagus, zucchini',
        'Sweet fruits like grapes, melons, coconut, sweet apples, pears',
        'Chicken (white meat), fish (freshwater), legumes, tofu',
        'Milk, ghee, fresh cheese (cooling and sweet)',
        'Coconut, sunflower, olive oil (cooling oils)',
        'Coriander, fennel, cardamom, mint, cilantro (cooling spices)'
      ],
      foodsToAvoid: [
        'Spicy, hot, pungent foods (chili, cayenne, hot peppers)',
        'Sour foods (citrus, tomatoes, vinegar, fermented foods)',
        'Salty foods (excess salt, pickles, processed foods)',
        'Fried and oily foods',
        'Alcohol and caffeine in excess',
        'Red meat and shellfish'
      ],
      mealTiming: [
        'Eat at regular times (Pitta types get irritable when hungry)',
        'Avoid eating when angry or stressed',
        'Favor cool or room temperature foods and drinks',
        'Eat in peaceful, pleasant environment',
        'Don\'t skip meals (maintains stable blood sugar)'
      ]
    },
    lifestyle: {
      exercise: [
        'Moderate intensity exercise',
        'Swimming, cycling, walking in nature',
        'Yoga (cooling practices, avoid hot yoga)',
        'Exercise during cooler parts of day (early morning or evening)',
        'Avoid competitive or aggressive sports when imbalanced'
      ],
      dailyRoutine: [
        'Consistent meal and sleep times',
        'Cool showers or baths',
        'Avoid excessive sun exposure',
        'Practice cooling pranayama (Sheetali, Sheetkari)',
        'Oil massage with coconut or sunflower oil'
      ]
    }
  },

  // KAPHA CONSTITUTION
  'kapha': {
    dietary: {
      foodsToFavor: [
        'Pungent (spicy), bitter, and astringent (stimulating tastes)',
        'Barley, buckwheat, corn, millet, quinoa, rye (light, dry grains)',
        'Leafy greens, broccoli, cauliflower, cabbage, radishes, onions, garlic',
        'Apples, pears, pomegranates, berries, cherries (astringent fruits)',
        'Lentils, mung beans, chicken, turkey, fish (light proteins)',
        'Ginger, black pepper, cayenne, turmeric, cinnamon, cloves (warming spices)',
        'Use minimal amounts - mustard, sunflower, or safflower oil'
      ],
      foodsToAvoid: [
        'Dairy products (milk, cheese, yogurt, ice cream)',
        'Sweet, heavy foods (bananas, melons, dates, sweets)',
        'Oily and fried foods',
        'Cold foods and iced drinks',
        'Wheat and rice (heavy grains)',
        'Nuts and seeds in excess',
        'Salty and sour foods'
      ],
      mealTiming: [
        'Eat only when truly hungry (don\'t eat from habit)',
        'Make lunch the largest meal of the day',
        'Light or skip breakfast if not hungry',
        'Early, light dinners',
        'Occasional fasting can be beneficial',
        'Favor warm, dry, light foods'
      ]
    },
    lifestyle: {
      exercise: [
        'Regular, vigorous exercise is essential',
        'Jogging, aerobics, weight training, dancing',
        'Exercise until breaking a sweat',
        'Morning exercise (6-10 AM) is ideal',
        'Competitive sports can be motivating',
        'Dynamic yoga practices (Sun Salutations, backbends)'
      ],
      dailyRoutine: [
        'Wake up early (5-6 AM) to avoid Kapha time sluggishness',
        'No daytime napping',
        'Dry brushing before bathing',
        'Energizing breathwork (Bhastrika, Right Nostril breathing)',
        'Stay active throughout the day',
        'Regular, stimulating activities'
      ]
    }
  },

  // VATA-PITTA CONSTITUTION
  'vata-pitta': {
    dietary: {
      foodsToFavor: [
        'Sweet, bitter, and astringent (balances both doshas)',
        'Rice, wheat, oats (grounding for Vata, cooling for Pitta)',
        'Sweet vegetables like squash, sweet potatoes, leafy greens',
        'Sweet, juicy fruits like grapes, melons, sweet apples, pears',
        'Chicken, fish, tofu, mung beans (easily digestible proteins)',
        'Milk, ghee, fresh cheese (cooling and nourishing)',
        'Coconut, olive oil (cooling but nourishing)'
      ],
      foodsToAvoid: [
        'Spicy, hot foods (aggravate Pitta)',
        'Dry, light foods like crackers (aggravate Vata)',
        'Fermented foods (too heating for Pitta)',
        'Excessive raw foods (hard to digest for Vata)',
        'Caffeine and alcohol (stimulating for both doshas)'
      ],
      mealTiming: [
        'Regular meal times are crucial',
        'Never skip meals (aggravates both doshas)',
        'Eat in calm, peaceful environment',
        'Favor warm, moist, slightly oily foods',
        'Avoid eating when stressed or rushed'
      ]
    },
    lifestyle: {
      exercise: [
        'Moderate, regular exercise',
        'Swimming, walking, gentle yoga',
        'Avoid overheating or overexertion',
        'Exercise during cooler parts of day',
        'Focus on consistency rather than intensity'
      ],
      dailyRoutine: [
        'Consistent sleep schedule (bed by 10 PM)',
        'Regular meal times',
        'Oil massage with cooling oils (coconut, sunflower)',
        'Cooling pranayama (Sheetali, Nadi Shodhana)',
        'Avoid excessive sun exposure'
      ]
    }
  },

  // PITTA-VATA CONSTITUTION
  'pitta-vata': {
    dietary: {
      foodsToFavor: [
        'Sweet, bitter, and astringent (balances both doshas)',
        'Rice, wheat, oats (grounding for Vata, cooling for Pitta)',
        'Sweet vegetables like squash, sweet potatoes, leafy greens',
        'Sweet, juicy fruits like grapes, melons, sweet apples, pears',
        'Chicken, fish, tofu, mung beans (easily digestible proteins)',
        'Milk, ghee, fresh cheese (cooling and nourishing)',
        'Coconut, olive oil (cooling but nourishing)'
      ],
      foodsToAvoid: [
        'Spicy, hot foods (aggravate Pitta)',
        'Dry, light foods like crackers (aggravate Vata)',
        'Fermented foods (too heating for Pitta)',
        'Excessive raw foods (hard to digest for Vata)',
        'Caffeine and alcohol (stimulating for both doshas)'
      ],
      mealTiming: [
        'Regular meal times are crucial',
        'Never skip meals (aggravates both doshas)',
        'Eat in calm, peaceful environment',
        'Favor warm, moist, slightly oily foods',
        'Avoid eating when stressed or rushed'
      ]
    },
    lifestyle: {
      exercise: [
        'Moderate, regular exercise',
        'Swimming, walking, gentle yoga',
        'Avoid overheating or overexertion',
        'Exercise during cooler parts of day',
        'Focus on consistency rather than intensity'
      ],
      dailyRoutine: [
        'Consistent sleep schedule (bed by 10 PM)',
        'Regular meal times',
        'Oil massage with cooling oils (coconut, sunflower)',
        'Cooling pranayama (Sheetali, Nadi Shodhana)',
        'Avoid excessive sun exposure'
      ]
    }
  },

  // PITTA-KAPHA CONSTITUTION
  'pitta-kapha': {
    dietary: {
      foodsToFavor: [
        'Bitter, astringent, and mildly pungent (balance both doshas)',
        'Barley, quinoa, millet, small amounts of rice',
        'Leafy greens, broccoli, cauliflower, asparagus, bitter gourd',
        'Apples, pears, pomegranates, berries (astringent fruits)',
        'Legumes (mung beans, lentils), lean poultry, fish',
        'Turmeric, coriander, fennel, cumin (mild warming spices)',
        'Small amounts of coconut or sunflower oil'
      ],
      foodsToAvoid: [
        'Dairy products (heavy for Kapha, heating for Pitta)',
        'Hot, spicy foods (aggravate Pitta)',
        'Sweet, heavy foods (increase Kapha)',
        'Sour foods like citrus, tomatoes, vinegar',
        'Fried and oily foods',
        'Red meat and processed foods',
        'Excessive salt'
      ],
      mealTiming: [
        'Moderate portions at regular times',
        'Avoid overeating (tends to increase both doshas)',
        'Eat largest meal at midday',
        'Light breakfast and dinner',
        'Room temperature or slightly warm foods'
      ]
    },
    lifestyle: {
      exercise: [
        'Regular, moderate to vigorous exercise',
        'Swimming, cycling, hiking, martial arts',
        'Avoid overheating during exercise',
        'Exercise in cooler parts of day (early morning, evening)',
        'Competitive sports in moderation',
        'Dynamic yoga with cooling pranayama'
      ],
      dailyRoutine: [
        'Rise early (6 AM) but not as early as pure Kapha types',
        'Regular meal and sleep schedules',
        'Oil massage with cooling oils 2-3 times per week',
        'Avoid daytime sleep',
        'Practice cooling breathing techniques',
        'Moderate sun exposure'
      ]
    }
  },

  // KAPHA-PITTA CONSTITUTION
  'kapha-pitta': {
    dietary: {
      foodsToFavor: [
        'Bitter, astringent, and mildly pungent (balance both doshas)',
        'Barley, quinoa, millet, small amounts of rice',
        'Leafy greens, broccoli, cauliflower, asparagus, bitter gourd',
        'Apples, pears, pomegranates, berries (astringent fruits)',
        'Legumes (mung beans, lentils), lean poultry, fish',
        'Turmeric, coriander, fennel, cumin (mild warming spices)',
        'Small amounts of coconut or sunflower oil'
      ],
      foodsToAvoid: [
        'Dairy products (heavy for Kapha, heating for Pitta)',
        'Hot, spicy foods (aggravate Pitta)',
        'Sweet, heavy foods (increase Kapha)',
        'Sour foods like citrus, tomatoes, vinegar',
        'Fried and oily foods',
        'Red meat and processed foods',
        'Excessive salt'
      ],
      mealTiming: [
        'Moderate portions at regular times',
        'Avoid overeating (tends to increase both doshas)',
        'Eat largest meal at midday',
        'Light breakfast and dinner',
        'Room temperature or slightly warm foods'
      ]
    },
    lifestyle: {
      exercise: [
        'Regular, moderate to vigorous exercise',
        'Swimming, cycling, hiking, martial arts',
        'Avoid overheating during exercise',
        'Exercise in cooler parts of day (early morning, evening)',
        'Competitive sports in moderation',
        'Dynamic yoga with cooling pranayama'
      ],
      dailyRoutine: [
        'Rise early (6 AM) but not as early as pure Kapha types',
        'Regular meal and sleep schedules',
        'Oil massage with cooling oils 2-3 times per week',
        'Avoid daytime sleep',
        'Practice cooling breathing techniques',
        'Moderate sun exposure'
      ]
    }
  },

  // KAPHA-VATA CONSTITUTION
  'kapha-vata': {
    dietary: {
      foodsToFavor: [
        'Pungent, bitter, and sweet (sweet in moderation to ground Vata)',
        'Quinoa, amaranth, small amounts of rice (for Vata), barley, millet',
        'Cooked leafy greens, root vegetables (in moderation), asparagus, broccoli',
        'Cooked apples, stewed pears, berries, pomegranates',
        'Mung beans, lentils, tofu, tempeh, small amounts of nuts and seeds',
        'Warming spices like ginger, cinnamon, cardamom, turmeric, cumin',
        'Small amounts of sesame or sunflower oil (warming for Vata, light for Kapha)'
      ],
      foodsToAvoid: [
        'Dairy products (increase Kapha and can be hard to digest for Vata)',
        'Cold, raw foods (aggravate both doshas)',
        'Excessive sweets (increase Kapha)',
        'Dry, light foods without oil (aggravate Vata)',
        'Frozen and leftover foods',
        'Carbonated drinks and caffeine'
      ],
      mealTiming: [
        'Regular meal times to pacify Vata',
        'Warm, freshly cooked meals',
        'Moderate portions to avoid overloading Kapha',
        'Avoid eating when anxious or rushed',
        'Sip warm water or herbal teas throughout the day'
      ]
    },
    lifestyle: {
      exercise: [
        'Regular, moderate exercise (not too vigorous for Vata, not too gentle for Kapha)',
        'Grounding activities like walking in nature, gentle jogging',
        'Yoga with both energizing and calming poses',
        'Swimming (if water is warm)',
        'Avoid overexertion which can aggravate Vata',
        'Consistency is more important than intensity'
      ],
      dailyRoutine: [
        'Wake up early (6 AM) but not as early as pure Kapha types',
        'Oil massage 2-3 times per week with warm sesame oil',
        'Regular sleep schedule (bed by 10 PM)',
        'Avoid daytime sleeping',
        'Gentle morning routine to ease into the day',
        'Evening relaxation practices'
      ]
    }
  },

  // VATA-KAPHA CONSTITUTION
  'vata-kapha': {
    dietary: {
      foodsToFavor: [
        'Pungent, bitter, and sweet (sweet in moderation to ground Vata)',
        'Quinoa, amaranth, small amounts of rice (for Vata), barley, millet',
        'Cooked leafy greens, root vegetables (in moderation), asparagus, broccoli',
        'Cooked apples, stewed pears, berries, pomegranates',
        'Mung beans, lentils, tofu, tempeh, small amounts of nuts and seeds',
        'Warming spices like ginger, cinnamon, cardamom, turmeric, cumin',
        'Small amounts of sesame or sunflower oil (warming for Vata, light for Kapha)'
      ],
      foodsToAvoid: [
        'Dairy products (increase Kapha and can be hard to digest for Vata)',
        'Cold, raw foods (aggravate both doshas)',
        'Excessive sweets (increase Kapha)',
        'Dry, light foods without oil (aggravate Vata)',
        'Frozen and leftover foods',
        'Carbonated drinks and caffeine'
      ],
      mealTiming: [
        'Regular meal times to pacify Vata',
        'Warm, freshly cooked meals',
        'Moderate portions to avoid overloading Kapha',
        'Avoid eating when anxious or rushed',
        'Sip warm water or herbal teas throughout the day'
      ]
    },
    lifestyle: {
      exercise: [
        'Regular, moderate exercise (not too vigorous for Vata, not too gentle for Kapha)',
        'Grounding activities like walking in nature, gentle jogging',
        'Yoga with both energizing and calming poses',
        'Swimming (if water is warm)',
        'Avoid overexertion which can aggravate Vata',
        'Consistency is more important than intensity'
      ],
      dailyRoutine: [
        'Wake up early (6 AM) but not as early as pure Kapha types',
        'Oil massage 2-3 times per week with warm sesame oil',
        'Regular sleep schedule (bed by 10 PM)',
        'Avoid daytime sleeping',
        'Gentle morning routine to ease into the day',
        'Evening relaxation practices'
      ]
    }
  },

  // TRIDOSHIC CONSTITUTION
  'tridoshic': {
    dietary: {
      foodsToFavor: [
        'Seasonal eating: Adjust diet based on current season and environmental conditions',
        'Variety of grains - rice, wheat, quinoa, barley as appropriate',
        'Wide variety of vegetables, prepared according to season and current needs',
        'Fresh, seasonal fruits in moderation',
        'Varied protein sources including legumes, dairy (if tolerated), lean meats',
        'Moderate use of all spices, adjusted for season and current balance',
        'Flexibility: Diet should change based on current predominant dosha'
      ],
      foodsToAvoid: [
        'Extreme foods (too hot, too cold, too spicy, too bland)',
        'Processed and artificial foods',
        'Overeating or undereating',
        'Foods that consistently aggravate any particular dosha',
        'Irregular eating patterns'
      ],
      mealTiming: [
        'Maintain regular meal times',
        'Eat according to hunger and season',
        'Pay attention to body\'s changing needs',
        'Moderate portions',
        'Mindful eating practices'
      ]
    },
    lifestyle: {
      exercise: [
        'Varied exercise routine that changes with seasons and needs',
        'Moderate intensity most of the time',
        'Include cardiovascular, strength, and flexibility training',
        'Yoga with varied practices',
        'Listen to body\'s daily energy levels',
        'Avoid extremes in exercise'
      ],
      dailyRoutine: [
        'Flexible but consistent basic routine',
        'Wake time between 6-7 AM',
        'Regular meal and sleep schedules with some flexibility',
        'Seasonal adjustments to routine',
        'Balance activity with rest',
        'Adaptability is key'
      ]
    }
  }
};
